import React, { useState, useMemo, useEffect } from "react";
import { Modal, Select, Button, Space } from "antd";
import { useQuery } from "react-query";
import { getMetaData } from "../../api/server";
import { useIntl } from "react-intl";
import { getEntityForm } from "../../api/server";
import { fillCompanyForm } from "../util/docsPrint";

/**
 * @typedef {Object} PrintFormsModalProps
 * @property {boolean} visible - Controls the visibility of the modal
 * @property {Function} onCancel - Callback function to handle modal closure
 * @property {string} entity - The type of entity (e.g., 'patient', 'employee', 'company')
 * @property {string} entityId - The unique identifier of the entity
 * @property {string} entityCollection - The collection name where the entity is stored
 */

/**
 * Modal component for selecting and printing entity-specific forms.
 * Provides a form selection interface and handles the printing process.
 *
 * @component
 * @param {PrintFormsModalProps} props - Component properties
 *
 * @example
 * ```jsx
 * <PrintFormsModal
 *   visible={showModal}
 *   onCancel={() => setShowModal(false)}
 *   entity="patient"
 *   entityId="123"
 *   entityCollection="patients"
 * />
 * ```
 *
 * @remarks
 * - Uses react-query for form data fetching
 * - Filters forms based on the provided entity type
 * - Supports form selection and printing functionality
 * - Integrates with the application's internationalization system
 */
const PrintFormsModal = ({
  visible,
  onCancel,
  entity,
  entityId,
  entityCollection,
}) => {
  const [selectedForm, setSelectedForm] = useState(null);
  const intl = useIntl();

  // Clear state when modal is hidden
  useEffect(() => {
    if (!visible) {
      setSelectedForm(null);
    }
  }, [visible]);

  // Fetch all forms
  const { data: response, isLoading } = useQuery("forms", () =>
    getMetaData({ name: "forms" })
  );
  const forms = response?.data?.items || [];

  // Filter forms by entity
  const filteredForms = useMemo(() => {
    return forms.filter((form) => form.entities?.includes(entity));
  }, [forms, entity]);

  const handlePrint = async () => {
    if (!selectedForm) return;

    try {
      await fillCompanyForm(selectedForm.form, selectedForm.Data);
      onCancel(); // Close modal after successful print
    } catch (error) {
      console.error("Error printing form:", error);
    }
  };

  const fetchForm = async (formId) => {
    const action = await getEntityForm({
      entityCollection,
      entityId,
      formId,
    }).then((response) => {
      setSelectedForm(response);
    });
  };

  return (
    <Modal
      title={intl.formatMessage({
        id: "app.settings.forms.print_form_modal_title",
      })}
      open={visible}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: "100%" }}>
        <Select
          style={{ width: "100%" }}
          loading={isLoading}
          placeholder={intl.formatMessage({
            id: "app.settings.forms.select_form",
          })}
          value={selectedForm?.id}
          onChange={(value) => fetchForm(value)}
          options={filteredForms.map((form) => ({
            value: form.id,
            label: form.name,
          }))}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
            marginTop: "16px",
          }}
        >
          <Button onClick={onCancel}>
            {intl.formatMessage({ id: "app.actions.cancel" })}
          </Button>
          <Button type="primary" disabled={!selectedForm} onClick={handlePrint}>
            {intl.formatMessage({ id: "app.actions.print" })}
          </Button>
        </div>
      </Space>
    </Modal>
  );
};

export default PrintFormsModal;
